import { z } from 'zod'
import { ROLES } from '../constants/permissions.js'

/**
 * Minimum viable password policy for an internal tool: length does most of the
 * work, so we require 10+ rather than a thicket of character-class rules.
 */
export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200, 'Password must be at most 200 characters')

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(255)

export const loginSchema = z.object({
  email: emailSchema,
  // Not `passwordSchema`: an existing password predating a policy change must
  // still be able to log in. Strength is enforced where passwords are *set*.
  password: z.string().min(1, 'Password is required').max(200),
})
export type LoginInput = z.infer<typeof loginSchema>

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required').max(200),
  newPassword: passwordSchema,
})
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

export const roleSchema = z.enum(ROLES)

/** The shape returned by `/api/auth/me` and the users endpoints. Never includes the hash. */
export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: roleSchema,
  active: z.boolean(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
})
export type PublicUser = z.infer<typeof publicUserSchema>

export const meResponseSchema = z.object({
  user: publicUserSchema,
  permissions: z.array(z.string()),
})
export type MeResponse = z.infer<typeof meResponseSchema>
